/**
 * Elo -> expected goals mapping, ported EXACTLY from the legacy `drawScore`
 * (continuity of predictions takes precedence over any "improvement").
 * Constants live in src/data/model-params.json and are injected as params.
 */
import type { PoissonParams, Rng, Score } from "./types.ts";

/**
 * Expected goals (lambda) per side for a given effective Elo gap.
 * `eloHome`/`eloAway` already include any home-advantage bonus.
 * Legacy: t = base + |dr|/500 ; la = max(.18,(t+dr/235)/2) ; lb = max(.18,(t-dr/235)/2)
 */
export function lambdas(
  eloHome: number,
  eloAway: number,
  p: PoissonParams,
): readonly [number, number] {
  const dr = eloHome - eloAway;
  const total = p.baseTotalGoals + Math.abs(dr) / p.totalGoalsEloDivisor;
  const lambdaHome = Math.max(p.lambdaMin, (total + dr / p.shareEloDivisor) / 2);
  const lambdaAway = Math.max(p.lambdaMin, (total - dr / p.shareEloDivisor) / 2);
  return [lambdaHome, lambdaAway];
}

/**
 * Poisson sampler, ported from the legacy `pois` (Knuth multiplication method,
 * goals capped at maxGoalsPerTeam) — but fed by an injected RNG for seedability.
 */
export function samplePoisson(lambda: number, rng: Rng, maxGoals: number): number {
  const limit = Math.exp(-lambda);
  let k = 0;
  let p = 1;
  do {
    k++;
    p *= rng();
  } while (p > limit);
  return Math.min(k - 1, maxGoals);
}

/** Draw a full-match score from the two independent Poisson laws. */
export function drawScore(
  eloHome: number,
  eloAway: number,
  p: PoissonParams,
  rng: Rng,
): Score {
  const [la, lb] = lambdas(eloHome, eloAway, p);
  return [samplePoisson(la, rng, p.maxGoalsPerTeam), samplePoisson(lb, rng, p.maxGoalsPerTeam)];
}

/**
 * Draw an extra-time score: per specs/03 §3, lambdas are scaled by 1/3
 * (30 minutes of play). The legacy did not simulate extra time — documented gap.
 */
export function drawExtraTimeScore(
  eloHome: number,
  eloAway: number,
  p: PoissonParams,
  rng: Rng,
): Score {
  const [la, lb] = lambdas(eloHome, eloAway, p);
  return [
    samplePoisson(la / 3, rng, p.maxGoalsPerTeam),
    samplePoisson(lb / 3, rng, p.maxGoalsPerTeam),
  ];
}

/** P(X = k) for X ~ Poisson(lambda) truncated by lumping the tail into maxGoals. */
export function poissonPmfCapped(k: number, lambda: number, maxGoals: number): number {
  if (k < 0 || k > maxGoals) return 0;
  const pmf = (n: number): number => {
    let v = Math.exp(-lambda);
    for (let i = 1; i <= n; i++) v *= lambda / i;
    return v;
  };
  if (k < maxGoals) return pmf(k);
  let below = 0;
  for (let i = 0; i < maxGoals; i++) below += pmf(i);
  return 1 - below;
}

/** Probability of an exact score under the model (independent capped Poissons). */
export function scoreProbability(
  score: Score,
  eloHome: number,
  eloAway: number,
  p: PoissonParams,
): number {
  const [la, lb] = lambdas(eloHome, eloAway, p);
  return (
    poissonPmfCapped(score[0], la, p.maxGoalsPerTeam) *
    poissonPmfCapped(score[1], lb, p.maxGoalsPerTeam)
  );
}

/**
 * 1X2 outcome probabilities (home win / draw / away win) under the model —
 * sum of the capped score grid. `eloHome`/`eloAway` already include any home
 * advantage. The three values sum to 1.
 */
export function outcomeProbabilities(
  eloHome: number,
  eloAway: number,
  p: PoissonParams,
): { homeWin: number; draw: number; awayWin: number } {
  const [la, lb] = lambdas(eloHome, eloAway, p);
  let home = 0;
  let draw = 0;
  let away = 0;
  for (let h = 0; h <= p.maxGoalsPerTeam; h++) {
    const ph = poissonPmfCapped(h, la, p.maxGoalsPerTeam);
    for (let a = 0; a <= p.maxGoalsPerTeam; a++) {
      const pr = ph * poissonPmfCapped(a, lb, p.maxGoalsPerTeam);
      if (h > a) home += pr;
      else if (h === a) draw += pr;
      else away += pr;
    }
  }
  const s = home + draw + away || 1;
  return { homeWin: home / s, draw: draw / s, awayWin: away / s };
}

/**
 * Dixon–Coles (1997) low-score dependence parameter ρ. PLACEHOLDER default —
 * the independent Poisson under-predicts draws; a small negative ρ boosts the
 * 0-0 / 1-1 cells and trims 1-0 / 0-1. The real value must be CALIBRATED on
 * historical results (that's what the scoring/backtest is for); −0.12 sits in
 * the usual fitted range (≈ −0.05 … −0.18).
 */
export const DIXON_COLES_RHO_DEFAULT = -0.12;

/**
 * Dixon–Coles τ correction on the four low-score cells. λ = home rate,
 * μ = away rate, ρ = dependence. τ = 1 everywhere else, so ρ = 0 ⇒ the model
 * collapses back to the independent Poisson (continuity guarantee).
 */
export function dixonColesTau(
  homeGoals: number,
  awayGoals: number,
  lambdaHome: number,
  lambdaAway: number,
  rho: number,
): number {
  if (homeGoals === 0 && awayGoals === 0) return 1 - lambdaHome * lambdaAway * rho;
  if (homeGoals === 0 && awayGoals === 1) return 1 + lambdaHome * rho;
  if (homeGoals === 1 && awayGoals === 0) return 1 + lambdaAway * rho;
  if (homeGoals === 1 && awayGoals === 1) return 1 - rho;
  return 1;
}

/** Exact-score probability under Dixon–Coles (independent grid × τ). Not
 *  normalised on its own — τ slightly shifts total mass; callers that need a
 *  distribution normalise over the grid (see outcomeProbabilitiesDc). */
export function scoreProbabilityDc(
  score: Score,
  eloHome: number,
  eloAway: number,
  p: PoissonParams,
  rho: number,
): number {
  const [la, lb] = lambdas(eloHome, eloAway, p);
  const base =
    poissonPmfCapped(score[0], la, p.maxGoalsPerTeam) *
    poissonPmfCapped(score[1], lb, p.maxGoalsPerTeam);
  return base * dixonColesTau(score[0], score[1], la, lb, rho);
}

/**
 * 1X2 outcome probabilities under Dixon–Coles — same capped grid as
 * `outcomeProbabilities`, each cell reweighted by τ then renormalised. With
 * ρ = 0 it is byte-for-byte the independent-Poisson result.
 */
export function outcomeProbabilitiesDc(
  eloHome: number,
  eloAway: number,
  p: PoissonParams,
  rho: number,
): { homeWin: number; draw: number; awayWin: number } {
  const [la, lb] = lambdas(eloHome, eloAway, p);
  let home = 0;
  let draw = 0;
  let away = 0;
  for (let h = 0; h <= p.maxGoalsPerTeam; h++) {
    const ph = poissonPmfCapped(h, la, p.maxGoalsPerTeam);
    for (let a = 0; a <= p.maxGoalsPerTeam; a++) {
      const pr = ph * poissonPmfCapped(a, lb, p.maxGoalsPerTeam) * dixonColesTau(h, a, la, lb, rho);
      if (h > a) home += pr;
      else if (h === a) draw += pr;
      else away += pr;
    }
  }
  const s = home + draw + away || 1;
  return { homeWin: home / s, draw: draw / s, awayWin: away / s };
}

/**
 * Most probable exact score. With independent Poissons this is the pair of
 * marginal modes; computed by scanning the capped grid to stay correct at the
 * cap boundary. Reproduces the legacy modal scenario (verified in tests).
 */
export function modalScore(eloHome: number, eloAway: number, p: PoissonParams): Score {
  let best: Score = [0, 0];
  let bestProb = -1;
  for (let h = 0; h <= p.maxGoalsPerTeam; h++) {
    for (let a = 0; a <= p.maxGoalsPerTeam; a++) {
      const prob = scoreProbability([h, a], eloHome, eloAway, p);
      if (prob > bestProb) {
        bestProb = prob;
        best = [h, a];
      }
    }
  }
  return best;
}

/** Win probability from the Elo logistic (legacy `pwin`), divisor 400. */
export function winProbability(eloHome: number, eloAway: number): number {
  return 1 / (1 + Math.pow(10, -(eloHome - eloAway) / 400));
}
